import { useEffect, useMemo, useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ICatalog from "@models/ICatalog";
import ICategory from "@models/ICategory";
import IUser from "@models/IUser";
import IGetCategoriesDTO from "@models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "@models/DTO/IOnGetCategoriesDTO";
import IGetCatalogsDTO from "@models/DTO/IGetCatalogsDTO";
import IOnGetCatalogsDTO from "@models/DTO/IOnGetCatalogsDTO";
import ISupplierGetUsersDTO from "@models/DTO/ISupplierGetUsersDTO";
import ISuppliersOnGetUsersDTO from "@models/DTO/ISuppliersOnGetUsersDTO";
import { IGetRepGroupsDTO } from "@models/DTO/IGetRepGroupsDTO";
import IOnGetRepGroupsDTO from "@models/DTO/IOnGetRepGroupsDTO";
import IGetRepGroupDTO from "@models/DTO/IGetRepGroupDTO";
import IOnGetRepGroupDTO from "@models/DTO/IOnGetRepGroupDTO";
import { IFilterSelectOption } from "@models/IFilterConfig";
import { IVendorsFilterLookups } from "@config/filters/vendorsFilters";
import { requestOnce, useBaseDto } from "@hooks/shared/useSocketLookups";

export interface IVendorFilterLists {
    categories: ICategory[];
    catalogs: ICatalog[];
    users: IUser[];
}

/** Rep group typeahead page size */
const REP_GROUP_LOOKUP_LIMIT = 10;

/**
 * Loads the option lists the Vendors filter config needs (account categories, catalogs with their price groups,
 * and supplier users when the supplier assigns sales people instead of rep groups) and exposes the rep group lookup.
 *
 * `lists` stays null until every list has settled, so the filter bar can wait for it: a field whose options are not
 * known yet would otherwise be missing at mount and drop its value from a deep link or the default preset.
 * A list that fails to load leaves its filter out instead of blocking the screen's first search.
 */
export function useVendorFilterData() {
    const { socket, supplierModel } = useAppContextNonNull();
    const baseDto = useBaseDto();

    const [lists, setLists] = useState<IVendorFilterLists | null>(null);

    useEffect(() => {
        let cancelled = false;

        const categoriesPayload: IGetCategoriesDTO = { ...baseDto(), onlyRootCategories: true, isVendorCategory: true };
        const categories = requestOnce<IOnGetCategoriesDTO>(
            socket,
            EndpointsEnum.CategoriesGetCategories,
            EndpointsEnum.CategoriesOnGetCategories,
            categoriesPayload,
            { matches: (data) => !!data.isVendorCategory }
        ).then((data) => data.categories || []);

        const catalogsPayload: IGetCatalogsDTO = { ...baseDto() };
        const catalogs = requestOnce<IOnGetCatalogsDTO>(
            socket,
            EndpointsEnum.CatalogsGetCatalogs,
            EndpointsEnum.CatalogsOnGetCatalogs,
            catalogsPayload
        ).then((data) => data.catalogs || []);

        const usersPayload: ISupplierGetUsersDTO = { ...baseDto(), queryString: "" };
        const users = supplierModel.has_rep_groups
            ? Promise.resolve<IUser[]>([])
            : requestOnce<ISuppliersOnGetUsersDTO>(
                  socket,
                  EndpointsEnum.SuppliersGetUsers,
                  EndpointsEnum.SuppliersOnGetUsers,
                  usersPayload
              ).then((data) => data.users || []);

        Promise.allSettled([categories, catalogs, users]).then(([categoriesResult, catalogsResult, usersResult]) => {
            if (cancelled) {
                return;
            }

            setLists({
                categories: categoriesResult.status === "fulfilled" ? categoriesResult.value : [],
                catalogs: catalogsResult.status === "fulfilled" ? catalogsResult.value : [],
                users: usersResult.status === "fulfilled" ? usersResult.value : [],
            });
        });

        return () => {
            cancelled = true;
        };
        // Once per screen mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    // ── Rep group typeahead + by-guid resolver ──
    const lookups = useMemo<IVendorsFilterLookups>(() => {
        // RepGroupOnGetRepGroups does not echo the request, so concurrent lookups resolve in reply order;
        // the typeahead field only applies the result of its latest lookup.
        const repGroups = (text: string): Promise<IFilterSelectOption[]> => {
            const payload: IGetRepGroupsDTO = { ...baseDto(), name: text, limit: REP_GROUP_LOOKUP_LIMIT };

            return requestOnce<IOnGetRepGroupsDTO>(
                socket,
                EndpointsEnum.RepGroupGetRepGroups,
                EndpointsEnum.RepGroupOnGetRepGroups,
                payload
            ).then((data) => (data.repGroups || []).map((repGroup) => ({ label: repGroup.name, value: repGroup.guid })));
        };

        const resolveRepGroup = (guid: string): Promise<IFilterSelectOption | undefined> => {
            const payload: IGetRepGroupDTO = { ...baseDto(), repGroupGuid: guid };

            return requestOnce<IOnGetRepGroupDTO>(socket, EndpointsEnum.RepGroupGetRepGroup, EndpointsEnum.RepGroupOnGetRepGroup, payload, {
                matches: (data) => !!data.repGroup && data.repGroup.guid === guid,
            }).then((data) => ({ label: data.repGroup.name, value: data.repGroup.guid }));
        };

        return { repGroups, resolveRepGroup };
    }, [socket, baseDto]);

    return { lists, lookups };
}
